import {
  ApiFactory,
  ApiRef,
  createApiFactory,
  createApiRef,
} from '@backstage/core-plugin-api';
import {
  isIDToken,
  OktaAuth,
  OktaAuthOptions,
  TokenParams,
} from '@okta/okta-auth-js';
import jwtDecode, { JwtPayload } from 'jwt-decode';
import { retryable } from '@eng-portal/czi-extensions-common';
import { LocalStorageCache } from '../helpers/localStorageCache';

export const oktaTokenApiRef: ApiRef<OktaTokenApi> = createApiRef<OktaTokenApi>(
  { id: 'okta.token-api' },
);

export const DEFAULT_ISSUER_URL = 'https://czi.okta.com';
export const DEFAULT_SCOPES = [
  'openid',
  'profile',
  'email',
  'groups',
  'offline_access',
];

export interface OktaTokenApi {
  getToken(
    clientId: string,
    issuerUrl?: string,
    scopes?: string[],
  ): Promise<string>;
}

export class OktaToken implements OktaTokenApi {
  static createDefaultApiFactory(): ApiFactory<OktaTokenApi, OktaToken, {}> {
    return createApiFactory({
      api: oktaTokenApiRef,
      deps: {},
      factory: () => new OktaToken(),
    });
  }

  async getToken(
    clientId: string,
    issuerUrl: string = DEFAULT_ISSUER_URL,
    scopes: string[] = DEFAULT_SCOPES,
  ): Promise<string> {
    const clientAuth = this.createClient(clientId, issuerUrl, scopes);
    const tokens = await clientAuth.tokenManager.getTokens();
    if (tokens.idToken && !this.isExpired(tokens.idToken.idToken)) {
      return tokens.idToken.idToken;
    }
    if (tokens.refreshToken) {
      try {
        const renewed = await clientAuth.tokenManager.renew('idToken');
        if (isIDToken(renewed)) {
          return renewed.idToken;
        }
      } catch {
        clientAuth.tokenManager.clear();
      }
    }
    const token = await this.generateToken(clientId, issuerUrl, scopes);
    if (!token) {
      throw new Error('Okta popup did not return an ID token');
    }
    return token;
  }

  // returns true if the token expires within the threshold
  isExpired(
    token: string,
    thresholdSeconds: number = 10 * 60,
    decoder = jwtDecode<JwtPayload>,
  ): boolean {
    const decoded = decoder(token);
    if (!decoded.exp) {
      throw new Error('Okta token did not have expected exp value');
    }

    const secondsSinceEpoch = Math.round(new Date().getTime() / 1000);
    return secondsSinceEpoch + thresholdSeconds > decoded.exp;
  }

  async generateToken(
    clientId: string,
    issuerUrl: string,
    scopes?: string[],
  ): Promise<string | undefined> {
    return retryable(async () => {
      const clientAuth = this.createClient(clientId, issuerUrl, scopes);
      const tokenConfig: TokenParams = {
        pkce: true,
        clientId,
        scopes: this.refreshScopes(scopes),
      };
      try {
        const { tokens } = await clientAuth.token.getWithPopup(tokenConfig);
        clientAuth.tokenManager.setTokens(tokens);
        const { idToken } = tokens;
        return idToken?.idToken;
      } catch (error) {
        console.error('Error generating Okta token:', error);
        throw error;
      }
    });
  }

  createClient(
    clientId: string,
    issuerUrl: string,
    scopes: string[] = DEFAULT_SCOPES,
  ): OktaAuth {
    const storageKey = [
      'okta-token',
      encodeURIComponent(clientId),
      encodeURIComponent(issuerUrl),
      encodeURIComponent(this.refreshScopes(scopes).join(',')),
    ].join('-');
    const oktaConfig: OktaAuthOptions = {
      clientId,
      issuer: issuerUrl,
      pkce: true,
      scopes: this.refreshScopes(scopes),
      tokenManager: {
        autoRenew: false,
        storage: new LocalStorageCache(),
        storageKey,
      },
    };
    return new OktaAuth(oktaConfig);
  }

  private refreshScopes(scopes: string[] = DEFAULT_SCOPES): string[] {
    return [...new Set([...scopes, 'offline_access'])];
  }
}
